import React from "react";
import Authority from "@/enum/Authority";
import AuthorityNameMap from "@/map/AuthorityNameMap";
import { useHistory } from "react-router";
import { NoPrinter } from "@/components";
import { Button, Modal, ModalFuncProps } from "antd";
import StorageManager from "@/utils/StorageManager";
import {
    AiKuCunPrintDownloadUrl,
    CNPrinterDownloadURL,
    ChannelShopPrintDownloadUrl,
    DyPrintDownloadUrl,
    FD_BASE_URL,
    JDPrinterDownloadUrl,
    KSPrintDownloadUrl,
    NextXiaoHongShuPrintDownloadUrl,
    PDDPrinterDownloadURL,
    SFPrintDownloadUrl,
    WeChatUrl,
    XiaoHongShuPrintDownloadUrl,
} from "@/config";
import { download, openUrl } from "@/utils";
import { qqService, wangwangService, weChatService } from "@/services/customerServices";
import ProxyImage from "@/components/ProxyImage";
import GlobalStore from "@/store";
import { when } from "mobx";
import { InfoCircleOutlined } from "@ant-design/icons";
import WaybillTemplatePrintType from "@/enum/WaybillTemplatePrintType";

const PrintDialogMapping = new Map<WaybillTemplatePrintType, any>();

async function showDownloadCainiaoPrintAgentDialog() {
    if (PrintDialogMapping.has(WaybillTemplatePrintType.CAINIAO)) {
        return;
    }

    const show = await ensureShowChromiumLocalNetworkPermission();

    if (show) return;

    const instance = Modal.confirm({
        title: "检测不到菜鸟打印组件，请按下面方法检查",
        width: 516,
        icon: false,
        content: <NoPrinter type="CaiNiao" />,
        okText: "点击下载打印控件",
        okButtonProps: {
            href: CNPrinterDownloadURL,
            target: "_blank",
        },
        afterClose() {
            PrintDialogMapping.delete(WaybillTemplatePrintType.CAINIAO);
        },
    });

    PrintDialogMapping.set(WaybillTemplatePrintType.CAINIAO, instance);
}

// 没有找到打印机，显示的提示
async function showDownloadPddPrintAgentDialog() {
    if (PrintDialogMapping.has(WaybillTemplatePrintType.PDD)) {
        return;
    }

    const show = await ensureShowChromiumLocalNetworkPermission();

    if (show) return;

    const instance = Modal.confirm({
        title: "检测不到拼多多打印组件，请按下面方法检查",
        icon: false,
        width: 516,
        content: <NoPrinter type="PDD" />,
        okText: "点击下载打印控件",
        okButtonProps: {
            href: PDDPrinterDownloadURL,
            target: "_blank",
        },
        afterClose() {
            PrintDialogMapping.delete(WaybillTemplatePrintType.PDD);
        },
    });

    PrintDialogMapping.set(WaybillTemplatePrintType.PDD, instance);
}

async function showDownloadJDPrintAgentDialog() {
    if (PrintDialogMapping.has(WaybillTemplatePrintType.JDL)) {
        return;
    }

    const show = await ensureShowChromiumLocalNetworkPermission();

    if (show) return;

    const instance = Modal.confirm({
        title: "检测不到京东打印组件，请按下面方法检查",
        icon: false,
        content: <NoPrinter type="JD" />,
        okText: "点击下载打印控件",
        width: 516,
        okButtonProps: {
            href: JDPrinterDownloadUrl,
            target: "_blank",
        },
        afterClose() {
            PrintDialogMapping.delete(WaybillTemplatePrintType.JDL);
        },
    });

    PrintDialogMapping.set(WaybillTemplatePrintType.JDL, instance);
}

async function showDownloadDyPrintAgentDialog() {
    if (PrintDialogMapping.has(WaybillTemplatePrintType.JINRITEMAI)) {
        return;
    }

    const show = await ensureShowChromiumLocalNetworkPermission();

    if (show) return;

    const instance = Modal.confirm({
        title: "检测不到抖店打印组件，请按下面方法检查",
        icon: false,
        content: <NoPrinter type="DY" />,
        okText: "点击下载打印控件",
        width: 516,
        okButtonProps: {
            href: DyPrintDownloadUrl,
            target: "_blank",
        },
        afterClose() {
            PrintDialogMapping.delete(WaybillTemplatePrintType.JINRITEMAI);
        },
    });

    PrintDialogMapping.set(WaybillTemplatePrintType.JINRITEMAI, instance);
}

export async function showDownloadKwaiPrintAgentDialog() {
    if (PrintDialogMapping.has(WaybillTemplatePrintType.KWAISHOP)) {
        return;
    }

    const show = await ensureShowChromiumLocalNetworkPermission();

    if (show) return;

    const instance = Modal.confirm({
        title: "检测不到快手打印组件，请按下面方法检查",
        icon: false,
        content: <NoPrinter type="KS" />,
        okText: "点击下载打印控件",
        width: 516,
        okButtonProps: {
            href: KSPrintDownloadUrl,
            target: "_blank",
        },
        afterClose() {
            PrintDialogMapping.delete(WaybillTemplatePrintType.KWAISHOP);
        },
    });

    PrintDialogMapping.set(WaybillTemplatePrintType.KWAISHOP, instance);
}

function showAuthorityDialog(history: ReturnType<typeof useHistory>, name: string, authority: Authority) {
    const push = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        e.preventDefault();

        history.push("/setting/user");

        modal.destroy();
    };

    const modal = Modal.warning({
        title: `您目前登录的账号【${name}】没有使用${AuthorityNameMap[authority]}权限`,
        okText: "确定",
        width: 500,
        content: (
            <div>
                <p className="mb10">如需要使用该功能请按下方步骤设置:</p>
                <p className="mb10">
                    1. 请让有账号管理权限的账号进入发货易中的“设置” — “
                    <a href="#" onClick={push}>
                        子账号管理(权限设置)
                    </a>
                    ” 中帮你设置开通该功能的权限
                </p>
                <p>2. 用当前账号重新登录发货易</p>
            </div>
        ),
    });
}

export function showIsInDevDialog() {
    function onWeChatClick() {
        Modal.info({
            title: <div className="tc">扫码发起聊天</div>,
            icon: false,
            width: 520,
            content: (
                <div className="tc">
                    <img src={weChatService?.url} />

                    <p className="mt10 text-gray">扫码关注公众号后,点击页面底部的「联系客服」发送消息</p>

                    <div style={{ display: "flex", justifyContent: "space-around", marginTop: 10 }}>
                        <Button
                            type="link"
                            onClick={() => {
                                window.open(WeChatUrl, "_blank");
                            }}
                        >
                            点击直接电脑端微信联系客服
                        </Button>

                        <Button
                            type="link"
                            href="https://qw15p3s8hv.feishu.cn/wiki/MzUjwDE6KiL3AAkeJolcRMzonvb"
                            target="_blank"
                        >
                            点击查看更多电脑端微信联系客服方法
                        </Button>
                    </div>
                </div>
            ),
        });
    }

    Modal.error({
        title: "您当前在测试环境，请不要操作，并去联系客服",
        content: (
            <p className="mt5">
                {weChatService && (
                    <a
                        target="_blank"
                        onClick={(e) => {
                            e.stopPropagation();

                            onWeChatClick();
                        }}
                        className="mr10"
                    >
                        <ProxyImage src={weChatService.icon} alt="点击这里给我发消息" title="点击这里给我发消息" />
                    </a>
                )}

                <a target="_blank" href={wangwangService.url} className="mr10">
                    <ProxyImage src={wangwangService.icon} alt="点击这里给我发消息" title="点击这里给我发消息" />
                </a>

                {qqService && (
                    <a target="_blank" href={qqService.url}>
                        <ProxyImage src={qqService.icon} alt="点击这里给我发消息" title="点击这里给我发消息" />
                    </a>
                )}
            </p>
        ),
        closable: false,
    });
}

/**
 * @see https://developer.chrome.com/blog/local-network-access?hl=zh-cn
 * @see https://docs.google.com/document/u/0/d/1QQkqehw8umtAgz5z0um7THx-aoU251p705FbIQjDuGs/mobilebasic?hl=zh-cn&pli=1&tab=t.0&_immersive_translate_auto_translate=1
 */
async function ensureShowChromiumLocalNetworkPermission() {
    const cacheKeyName = "CHROMIUM_LOCAL_NETWORK_PERMISSION";

    if (localStorage.getItem(cacheKeyName)) return false;

    try {
        // @ts-ignore
        if (navigator.userAgentData) {
            // @ts-ignore
            const { brands } = navigator.userAgentData;

            const chromium = brands.find((it) => it.brand === "Chromium");

            if (chromium) {
                const mainVersion = Number(chromium.version.substring(0, 3));

                if (!Number.isNaN(chromium.version.substring(0, 3)) && mainVersion >= 147) {
                    // @ts-ignore
                    const { state } = await navigator.permissions.query({ name: "local-network-access" });

                    if (state === "denied" || state === "prompt") {
                        Modal.confirm({
                            title: <p className="fb">当前版本浏览器可能限制访问打印组件，请参考下图开启相关权限</p>,
                            width: 650,
                            icon: false,
                            content: (
                                <div>
                                    <img
                                        className="w"
                                        src="https://img.alicdn.com/imgextra/i3/54458674/O1CN01AwWweS2DwkbogXj7S_!!54458674.png"
                                        alt=""
                                    />
                                </div>
                            ),
                            okText: "查看详细教程",
                            onOk() {
                                openUrl("https://qw15p3s8hv.feishu.cn/wiki/SOW9wrTnziP3dok1FMmcHa1yn6c");
                            },

                            cancelText: "已开启，不再提示",
                            onCancel() {
                                localStorage.setItem(cacheKeyName, "1");
                            },
                        });

                        return true;
                    }
                }
            }
        }
    } catch (e) {
        console.error(e);
    }

    return false;
}

async function showDownloadSfPrintAgentDialog() {
    if (PrintDialogMapping.has(WaybillTemplatePrintType.SF)) {
        return;
    }

    const show = await ensureShowChromiumLocalNetworkPermission();

    if (show) return;

    const instance = Modal.confirm({
        title: "检测不到顺丰打印组件，请按下面方法检查",
        width: 516,
        icon: false,
        content: <NoPrinter type="SF" />,
        okText: "点击下载打印控件",
        okButtonProps: {
            href: SFPrintDownloadUrl,
            target: "_blank",
        },
        afterClose() {
            PrintDialogMapping.delete(WaybillTemplatePrintType.SF);
        },
    });

    PrintDialogMapping.set(WaybillTemplatePrintType.SF, instance);
}

async function showDownloadChannelShopPrintAgentDialog() {
    if (PrintDialogMapping.has(WaybillTemplatePrintType.CHANNEL_SHOP)) {
        return;
    }

    const show = await ensureShowChromiumLocalNetworkPermission();

    if (show) return;

    const instance = Modal.confirm({
        title: "检测不到微信小店打印组件，请按下面方法检查",
        width: 516,
        icon: false,
        content: <NoPrinter type="CHANNEL_SHOP" />,
        okText: "点击下载打印控件",
        okButtonProps: {
            href: ChannelShopPrintDownloadUrl,
            target: "_blank",
        },
        afterClose() {
            PrintDialogMapping.delete(WaybillTemplatePrintType.CHANNEL_SHOP);
        },
    });

    PrintDialogMapping.set(WaybillTemplatePrintType.CHANNEL_SHOP, instance);
}

async function showDownloadXiaoHongShuPrintAgentDialog() {
    if (PrintDialogMapping.has(WaybillTemplatePrintType.XIAOHONGSHU)) {
        return;
    }

    const show = await ensureShowChromiumLocalNetworkPermission();

    if (show) return;

    const instance = Modal.confirm({
        title: "检测不到小红书打印组件，请按下面方法检查",
        width: 516,
        icon: false,
        content: <NoPrinter type="XIAOHONGSHU" />,
        okText: "点击下载打印控件",
        okButtonProps: {
            href: XiaoHongShuPrintDownloadUrl,
            target: "_blank",
        },
        afterClose() {
            PrintDialogMapping.delete(WaybillTemplatePrintType.XIAOHONGSHU);
        },
    });

    PrintDialogMapping.set(WaybillTemplatePrintType.XIAOHONGSHU, instance);
}

export async function showDownloadNextXiaoHongShuPrintAgentDialog() {
    if (PrintDialogMapping.has(WaybillTemplatePrintType.XIAOHONGSHU)) {
        return;
    }

    const show = await ensureShowChromiumLocalNetworkPermission();

    if (show) return;

    const instance = Modal.confirm({
        title: "检测不到新版小红书打印组件，请按下面方法检查",
        width: 516,
        icon: false,
        content: <NoPrinter type="NEXT_XIAOHONGSHU" />,
        okText: "点击下载打印控件",
        okButtonProps: {
            href: NextXiaoHongShuPrintDownloadUrl,
            target: "_blank",
        },
        afterClose() {
            PrintDialogMapping.delete(WaybillTemplatePrintType.XIAOHONGSHU);
        },
    });

    PrintDialogMapping.set(WaybillTemplatePrintType.XIAOHONGSHU, instance);
}

export async function showDownloadAiKuCunPrintAgentDialog() {
    if (PrintDialogMapping.has(WaybillTemplatePrintType.AIKUCUN)) {
        return;
    }

    const show = await ensureShowChromiumLocalNetworkPermission();

    if (show) return;

    const instance = Modal.confirm({
        title: "检测不到梦饷打印组件，请按下面方法检查",
        width: 516,
        icon: false,
        content: <NoPrinter type="AIKUCUN" />,
        okText: "点击下载打印控件",
        okButtonProps: {
            href: AiKuCunPrintDownloadUrl,
            target: "_blank",
        },
        afterClose() {
            PrintDialogMapping.delete(WaybillTemplatePrintType.AIKUCUN);
        },
    });

    PrintDialogMapping.set(WaybillTemplatePrintType.AIKUCUN, instance);
}

interface ShowDeprecatedCainiaoModalParams extends ModalFuncProps {
    version: string;
}

export async function showDeprecatedCainiaoModal({ version, ...rest }: ShowDeprecatedCainiaoModalParams) {
    await when(() => !!GlobalStore.currentUser);

    Modal.warning({
        ...rest,
        width: 600,
        title: (
            <span>
                根据淘宝官方要求，检测到您的菜鸟打印组件版本过低，后续会严重 影响打印。请按如下操作下载新的打印组件，
                <span className="error">当前版本:{version}</span>
            </span>
        ),
        content: (
            <div>
                <p className="mt20 fb">
                    1.下载最新版CAINIAO打印组件
                    <Button
                        type="primary"
                        onClick={() => {
                            window.open("https://page.cainiao.com/waybill/cloud_printing/home.html", "_blank");
                        }}
                    >
                        下载打印组件
                    </Button>
                </p>
                <p className="mt20 fb">2.下载后进行安装</p>
            </div>
        ),
    });
}

export function showQnExpiredModal() {
    Modal.info({
        title: "当前页面已失效并无法使用，请关闭掉本页面",
        icon: <InfoCircleOutlined style={{ color: "#ff4d4f" }} />,
        closable: false,
        keyboard: false,
        okButtonProps: {
            hidden: true,
        },
    });
}

export function showChannelShopDisabledUpdateAddressModal() {
    Modal.error({
        title: "微信小店修改地址提示",
        content: (
            <span>
                微信小店已发货，已完成和已取消订单修改地址后无法使用微信小店电子面单打印新地址，因此不允许修改。如需要打印修改后的地址，可以参考教程
                <a
                    href="https://qw15p3s8hv.feishu.cn/wiki/P1r0wQ3eDioIw1kvxhEcw0bRnZe?from=from_copylink"
                    target="_blank"
                >
                    如何修改微信小店已发货已完成订单地址并打印
                </a>
            </span>
        ),
    });
}

export async function shippedOrderCancelEwaybillTip() {
    await new Promise((yes, no) => {
        Modal.info({
            title: (
                <div>
                    已发货订单仅在软件内回收单号可能被<span className="error">平台判定虚假发货</span>
                </div>
            ),
            content: (
                <div>
                    请先前往商家后台成功修改发货运单号再回软件内操作回收单号
                    <a
                        href="https://qw15p3s8hv.feishu.cn/wiki/NtlOw6gxviu2FJkOn2gcCHIOnvu?from=from_copylink"
                        target="_blank"
                        className="ml5"
                    >
                        查看各平台已发货订单修改运单号说明教程 
                    </a>
                </div>
            ),
            okCancel: true,
            onOk() {
                yes(null);
            },
            onCancel() {
                no("用户取消操作");
            },
        });
    });
}

export function showWrongOrderStateModal(message: string) {
    Modal.error({
        title: "订单数据加载异常，请联系客服反馈",
        width: 600,
        content: (
            <div>
                <div className="error">{message}</div>
                <div className="mt10">
                    <Button
                        type="primary"
                        danger
                        onClick={() => {
                            openUrl("https://v2.fahuoyi.com/cs");
                        }}
                    >
                        联系客服
                    </Button>
                </div>
            </div>
        ),
        okText: "关闭",
    });
}

export {
    showDownloadCainiaoPrintAgentDialog,
    showDownloadPddPrintAgentDialog,
    showDownloadJDPrintAgentDialog,
    showAuthorityDialog,
    showDownloadDyPrintAgentDialog,
    showDownloadSfPrintAgentDialog,
    showDownloadChannelShopPrintAgentDialog,
    showDownloadXiaoHongShuPrintAgentDialog,
};
